// =============================================================================
// ui/components/YourDataCard.tsx — "Download my data", the ONE of them.
// -----------------------------------------------------------------------------
// ── 🔴 WHY THIS IS A SHARED COMPONENT AND NOT A SECOND BUTTON ──
//
// On 2026-09-08 a whole second export was built, merged and deployed —
// `io/backup/exportBook.ts` and a `BackupExport` card — because a `grep` for
// "downloadJSON|exportData|data.json" found nothing and an ABSENCE WAS ASSERTED
// FROM A QUERY NOBODY CHECKED AGAINST A KNOWN-PRESENT CASE. The export had been
// here for weeks under a different name.
//
// ⚠ The two files disagreed about what "your data" means — different table
// lists, different exclusions, different failure behaviour — and a user could
// have taken one of each and found them different sizes. Reverted; this is what
// replaced it.
//
// ▶ SO: Settings → Backup & Restore and My Account render THE SAME COMPONENT
// reading THE SAME table list. Two routes to one answer. If a third surface ever
// needs it, import this — do not write another.
// =============================================================================

import { useState } from 'react';
import { useSupabase } from '../state/supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';
import {
  buildAccountExport, exportFilename, exportRowCount, failedTables,
} from '../../io/sync/exportAccount';

/**
 * ── ⚠ THE CARD SAYS WHAT IS NOT IN THE FILE, AND THAT IS THE POINT ──
 *
 * This is the download the Danger Zone tells you to take before deleting your
 * account — the one irreversible action in the product. The shipping client's
 * version of this card promises "everything in your cloud data" while quietly
 * omitting every setting the user ever configured (see `io/sync/exportAccount`).
 *
 * So the blurb here names the two real exclusions — the shared instrument
 * catalogue, and the image and audio FILES, which are not rows — rather than
 * making a claim the file cannot honour. The file repeats them in its own `meta`.
 */
export function YourDataCard({ user }: { user: { id: string; email: string } }) {
  const { sb } = useSupabase();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    { rows: number; failed: string[] } | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const now = new Date().toISOString();
      const data = await buildAccountExport(sb, user, now, 'tradecoach-app');

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename(now);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // ⚠ Revoked, or the whole export stays in memory for the life of the tab —
      // and on a large book that is tens of megabytes held for nothing.
      URL.revokeObjectURL(url);

      // ⚠ Reports the ROW COUNT and any table that failed. "Export downloaded"
      // on its own is true of a file containing seventeen error stubs.
      setResult({ rows: exportRowCount(data), failed: failedTables(data) });
    } catch (e) {
      setError(reportFailure('export account', e, 'The export could not be built.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h3>Your data</h3>
      <p className="muted">
        A JSON copy of everything you have here — trades, journal entries,
        patterns, exit models, accounts, goals, and every setting.
      </p>
      <p className="muted">
        Not included: the shared instrument catalogue, which is the same for
        everyone, and your screenshots and voice notes, which are files rather
        than rows — the file lists their names. The download itself repeats this
        list.
      </p>

      {error ? <p className="red" role="alert">{error}</p> : null}

      {result ? (
        <p className={result.failed.length ? 'red' : 'muted'} role="status">
          {result.failed.length ? (
            <>
              <b>Downloaded, but it is incomplete.</b>{' '}
              {result.rows.toLocaleString()} rows saved.{' '}
              {result.failed.length === 1 ? 'This table' : 'These tables'} could
              not be read and appear in the file as an error:{' '}
              {result.failed.join(', ')}.
            </>
          ) : (
            <>Downloaded — {result.rows.toLocaleString()} rows.</>
          )}
        </p>
      ) : null}

      <button type="button" className="btn" disabled={busy} onClick={() => void run()}>
        {busy ? 'Building your export…' : 'Download my data (.json)'}
      </button>
    </section>
  );
}
